"use client"

import type { UseMutationResult } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"
import { CreditCardIcon } from "lucide-react"
import type { UseFormReturn } from "react-hook-form"
import { getCartItems } from "@/api/cart"
import type {
  CheckoutPaymentInstallment,
  CheckoutSubmitRequest,
  CheckoutSubmitResponse,
} from "@/api/checkout"
import { getCheckoutOrderSummary } from "@/api/checkout"
import { Button } from "@/components/ui/button"
import type { CheckoutFormValues } from "~/app/(checkout)/finalizar-comprar/page"

interface PaymentSectionProps {
  form: UseFormReturn<CheckoutFormValues>
  submitCheckoutMutation: UseMutationResult<
    CheckoutSubmitResponse,
    Error,
    CheckoutSubmitRequest,
    { toastId: string | number }
  >
}

export const getPaymentInstallmentOptions = (
  installments: CheckoutPaymentInstallment[] = []
): Array<{ value: string; label: string }> =>
  installments.map((installment) => ({
    value: installment.value,
    label: installment.label,
  }))

export const toCheckoutSubmitPayload = (
  values: CheckoutFormValues
): CheckoutSubmitRequest => {
  const billingCity =
    values.billing.city.trim() || values.shipping.city.trim() || "Sao Paulo"
  const basePayload = {
    shipToDifferentAddress: values.shipToDifferentAddress,
    billing: {
      firstName: values.billing.firstName,
      lastName: values.billing.lastName,
      email: values.billing.email,
      phone: values.billing.phone,
      cpf: values.billing.cpf,
      country: "BR",
      state: values.billing.state,
      city: billingCity,
      postcode: values.billing.postcode,
      address1: values.billing.address1,
      address2: values.billing.address2,
      number: values.billing.number,
      neighborhood: values.billing.district,
    },
    shipping: values.shipToDifferentAddress
      ? {
          firstName: values.billing.firstName,
          lastName: values.billing.lastName,
          country: values.shipping.country,
          state: values.shipping.state,
          city: values.shipping.city,
          postcode: values.shipping.postcode,
          address1: values.shipping.address1 || values.billing.address1,
          address2: values.shipping.address2 || values.billing.address2,
          number: values.shipping.number || values.billing.number,
          neighborhood: values.shipping.neighborhood || values.billing.district,
        }
      : undefined,
    orderNotes: values.billing.notes,
    termsAccepted: true,
  }

  if (values.payment.method === "triibo-payments-card") {
    return {
      ...basePayload,
      paymentMethod: "triibo-payments-card",
      card: {
        holderName: values.payment.card.holderName,
        holderCpf: values.payment.card.holderCpf,
        holderPhone: values.payment.card.holderPhone,
        number: values.payment.card.number,
        expiry: values.payment.card.expiry,
        cvc: values.payment.card.cvc,
        brand: values.payment.card.brand,
        installment: values.payment.card.installment,
      },
    }
  }

  if (values.payment.method === "triibo-payments-pix") {
    return {
      ...basePayload,
      paymentMethod: "triibo-payments-pix",
    }
  }

  return {
    ...basePayload,
    paymentMethod: "bacs",
  }
}

export const PaymentSection = ({
  form,
  submitCheckoutMutation,
}: PaymentSectionProps) => {
  const selectedPaymentMethod = form.watch("payment.method")
  const isCardSelected = selectedPaymentMethod === "triibo-payments-card"
  const cartQuery = useQuery({
    queryKey: ["cart", "items"],
    queryFn: getCartItems,
    retry: false,
  })
  const checkoutShippingAddress = {
    country: cartQuery.data?.shippingAddress?.country ?? "BR",
    state: cartQuery.data?.shippingAddress?.state ?? "SP",
    city: cartQuery.data?.shippingAddress?.city ?? "",
    postcode: cartQuery.data?.shippingAddress?.postcode ?? "",
  }
  const orderReviewQuery = useQuery({
    queryKey: [
      "checkout",
      "order-review",
      checkoutShippingAddress.country,
      checkoutShippingAddress.state,
      checkoutShippingAddress.city,
      checkoutShippingAddress.postcode,
    ],
    queryFn: () => getCheckoutOrderSummary(checkoutShippingAddress),
    enabled: isCardSelected,
    retry: false,
  })
  const installmentOptions = getPaymentInstallmentOptions(
    orderReviewQuery.data?.paymentInstallments
  )
  const submitError =
    submitCheckoutMutation.data && !submitCheckoutMutation.data.ok
      ? submitCheckoutMutation.data.message?.trim() ||
        "Nao foi possivel finalizar compra."
      : null

  const isSubmitReady = form.formState.isValid
  const submitTooltipMessage = isSubmitReady
    ? null
    : "Preencha os campos obrigatorios de cobranca para continuar."
  const isSubmitButtonDisabled =
    submitCheckoutMutation.isPending || !isSubmitReady

  return (
    <section className='space-y-4 rounded-2xl border border-zinc-200 bg-white p-5 shadow-xs sm:p-6'>
      <h2 className='font-semibold text-lg text-zinc-900 tracking-tight sm:text-xl'>
        Pagamento
      </h2>

      <div className='space-y-3'>
        <label className='flex cursor-pointer items-center gap-2 rounded-lg border border-zinc-300 px-3 py-2 text-sm'>
          <input
            {...form.register("payment.method")}
            type='radio'
            value='bacs'
          />
          <CreditCardIcon aria-hidden='true' size={15} />
          Transferencia Bancaria (BACS)
        </label>

        <label className='flex cursor-pointer items-center gap-2 rounded-lg border border-zinc-300 px-3 py-2 text-sm'>
          <input
            {...form.register("payment.method")}
            type='radio'
            value='triibo-payments-pix'
          />
          PIX (Triibo Payments)
        </label>

        <label className='flex cursor-pointer items-center gap-2 rounded-lg border border-zinc-300 px-3 py-2 text-sm'>
          <input
            {...form.register("payment.method")}
            type='radio'
            value='triibo-payments-card'
          />
          Cartao de credito (Triibo Payments)
        </label>

        {isCardSelected ? (
          <div className='grid gap-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3 sm:grid-cols-2'>
            <input
              {...form.register("payment.card.holderName")}
              className='h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm sm:col-span-2'
              placeholder='Nome no cartao'
            />
            <input
              {...form.register("payment.card.number")}
              className='h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm sm:col-span-2'
              placeholder='Numero do cartao'
            />
            <input
              {...form.register("payment.card.holderCpf")}
              className='h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm'
              placeholder='CPF do titular'
            />
            <input
              {...form.register("payment.card.holderPhone")}
              className='h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm'
              placeholder='Telefone do titular'
            />
            <input
              {...form.register("payment.card.expiry")}
              className='h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm'
              placeholder='Validade (MM/AA)'
            />
            <input
              {...form.register("payment.card.cvc")}
              className='h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm'
              placeholder='CVC'
            />
            <input
              {...form.register("payment.card.brand")}
              className='h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm'
              placeholder='Bandeira'
            />
            <select
              {...form.register("payment.card.installment")}
              className='h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm sm:col-span-2'
              disabled={
                orderReviewQuery.isLoading || installmentOptions.length === 0
              }
            >
              <option value=''>Selecione o parcelamento</option>
              {installmentOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <p className='rounded-lg border border-zinc-200 px-3 py-2 text-sm text-zinc-600'>
          BACS continua disponivel como fallback caso prefira transferencia.
        </p>
      </div>

      {submitError ? (
        <p className='rounded-xl border border-destructive/50 bg-destructive/5 px-3 py-2 text-destructive text-sm'>
          {submitError}
        </p>
      ) : null}

      <div title={submitTooltipMessage ?? undefined}>
        <Button
          className='h-12 w-full font-semibold uppercase tracking-[0.05em]'
          disabled={isSubmitButtonDisabled}
          onClick={form.handleSubmit((values) => {
            submitCheckoutMutation.mutate(toCheckoutSubmitPayload(values))
          })}
          size='lg'
          type='button'
        >
          {submitCheckoutMutation.isPending
            ? "Processando..."
            : "Efetuar pagamento"}
        </Button>
      </div>

      <p className='text-center text-muted-foreground text-xs'>
        Seus dados estao protegidos com criptografia e processamento seguro.
      </p>
    </section>
  )
}
